import React from 'react';

interface LogoProps {
  size?: number;
  variant?: 'full' | 'icon' | 'badge';
  className?: string;
}

// Logo Concept: Welding helmet visor with "stacked dimes" weld bead pattern
// The visor shape contains horizontal lines representing perfect TIG weld beads (stacked dimes)
// Colors: Orange/red gradient for the welding heat, cyan accents for the arc

export const Logo: React.FC<LogoProps> = ({ size = 48, variant = 'icon', className = '' }) => {
  if (variant === 'badge') {
    // Circular badge version with welding helmet and dime pattern
    return (
      <svg
        width={size}
        height={size}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
      >
        <defs>
          <linearGradient id="badge-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#dc2626" />
          </linearGradient>
          <linearGradient id="arc-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Outer circle */}
        <circle cx="50" cy="50" r="48" fill="#0a0a0f" stroke="url(#badge-gradient)" strokeWidth="2"/>

        {/* Inner decorative ring */}
        <circle cx="50" cy="50" r="42" fill="none" stroke="#ffffff" strokeWidth="0.5" strokeOpacity="0.1"/>

        {/* Welding helmet shape */}
        <path
          d="M30 35 C30 28, 38 22, 50 22 C62 22, 70 28, 70 35 L70 55 C70 65, 62 72, 50 72 C38 72, 30 65, 30 55 Z"
          fill="#1a1a1f"
          stroke="#333"
          strokeWidth="1"
        />

        {/* Visor (viewing window) */}
        <rect x="35" y="32" width="30" height="18" rx="3" fill="#0f172a" stroke="url(#arc-gradient)" strokeWidth="1.5"/>

        {/* Stacked dimes pattern inside visor (perfect weld beads) */}
        <g opacity="0.9">
          <ellipse cx="50" cy="36" rx="12" ry="2" fill="url(#badge-gradient)"/>
          <ellipse cx="50" cy="40" rx="12" ry="2" fill="url(#badge-gradient)"/>
          <ellipse cx="50" cy="44" rx="12" ry="2" fill="url(#badge-gradient)"/>
        </g>

        {/* Arc/spark effect */}
        <g filter="url(#glow)">
          <circle cx="65" cy="30" r="3" fill="#22d3ee"/>
          <path d="M65 27 L67 23 M68 30 L72 28 M65 33 L68 36" stroke="#22d3ee" strokeWidth="1.5" strokeLinecap="round"/>
        </g>

        {/* "DD" text at bottom */}
        <text
          x="50"
          y="85"
          textAnchor="middle"
          fill="url(#badge-gradient)"
          fontSize="12"
          fontFamily="Bebas Neue, sans-serif"
          letterSpacing="2"
        >
          DANNY DIME
        </text>
      </svg>
    );
  }

  if (variant === 'full') {
    // Full logo with text
    return (
      <svg
        width={size * 3}
        height={size}
        viewBox="0 0 180 60"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
      >
        <defs>
          <linearGradient id="full-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f97316" />
            <stop offset="100%" stopColor="#dc2626" />
          </linearGradient>
          <linearGradient id="full-arc" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="100%" stopColor="#3b82f6" />
          </linearGradient>
          <filter id="full-glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Icon part */}
        <g transform="translate(5, 5)">
          {/* Rounded square background */}
          <rect width="50" height="50" rx="12" fill="url(#full-gradient)"/>

          {/* Welding helmet silhouette */}
          <path
            d="M15 18 C15 14, 20 10, 25 10 C30 10, 35 14, 35 18 L35 32 C35 38, 30 42, 25 42 C20 42, 15 38, 15 32 Z"
            fill="#0a0a0f"
          />

          {/* Visor with stacked dimes */}
          <rect x="18" y="16" width="14" height="10" rx="2" fill="#1e293b"/>
          <ellipse cx="25" cy="19" rx="5" ry="1.2" fill="#f97316"/>
          <ellipse cx="25" cy="22" rx="5" ry="1.2" fill="#f97316"/>

          {/* Arc spark */}
          <g filter="url(#full-glow)">
            <circle cx="38" cy="15" r="2.5" fill="#22d3ee"/>
            <path d="M38 12 L40 9 M41 15 L44 14" stroke="#22d3ee" strokeWidth="1.2" strokeLinecap="round"/>
          </g>
        </g>

        {/* Text */}
        <text
          x="65"
          y="38"
          fill="white"
          fontSize="28"
          fontFamily="Bebas Neue, sans-serif"
          letterSpacing="2"
        >
          DANNY DIME
        </text>
        <text
          x="65"
          y="50"
          fill="#6b7280"
          fontSize="10"
          fontFamily="Oswald, sans-serif"
          letterSpacing="3"
        >
          AI WELDING COACH
        </text>
      </svg>
    );
  }

  // Default: Icon only version
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id="icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
        <linearGradient id="icon-arc" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#22d3ee" />
          <stop offset="100%" stopColor="#3b82f6" />
        </linearGradient>
        <filter id="icon-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.5" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>

      {/* Rounded square background */}
      <rect width="60" height="60" rx="14" fill="url(#icon-gradient)"/>

      {/* Welding helmet silhouette */}
      <path
        d="M18 22 C18 16, 24 11, 30 11 C36 11, 42 16, 42 22 L42 38 C42 46, 36 51, 30 51 C24 51, 18 46, 18 38 Z"
        fill="#0a0a0f"
      />

      {/* Visor window */}
      <rect x="22" y="19" width="16" height="12" rx="2" fill="#1e293b" stroke="#334155" strokeWidth="0.5"/>

      {/* Stacked dimes weld pattern in visor */}
      <ellipse cx="30" cy="22.5" rx="6" ry="1.5" fill="#fb923c"/>
      <ellipse cx="30" cy="26" rx="6" ry="1.5" fill="#f97316"/>
      <ellipse cx="30" cy="29.5" rx="6" ry="1.5" fill="#ea580c"/>

      {/* Welding arc/spark effect */}
      <g filter="url(#icon-glow)">
        <circle cx="46" cy="18" r="3" fill="#22d3ee"/>
        <path
          d="M46 14 L48 10 M49 18 L53 17 M46 22 L49 25"
          stroke="#22d3ee"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </g>

      {/* Subtle reflection on helmet */}
      <path
        d="M20 25 Q22 20, 26 18"
        stroke="white"
        strokeWidth="1"
        strokeOpacity="0.2"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
};

// Alternative: Minimalist "D" with stacked dimes
export const LogoMinimal: React.FC<LogoProps> = ({ size = 48, className = '' }) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <defs>
        <linearGradient id="minimal-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f97316" />
          <stop offset="100%" stopColor="#dc2626" />
        </linearGradient>
      </defs>

      {/* Rounded square background */}
      <rect width="60" height="60" rx="14" fill="url(#minimal-gradient)"/>

      {/* Stylized "D" made of stacked dimes (weld beads) */}
      <g fill="#0a0a0f">
        {/* Vertical bar of D */}
        <rect x="16" y="12" width="8" height="36" rx="2"/>

        {/* Curved part made of stacked ellipses (dimes) */}
        <ellipse cx="32" cy="16" rx="10" ry="3"/>
        <ellipse cx="36" cy="22" rx="8" ry="3"/>
        <ellipse cx="38" cy="30" rx="6" ry="3"/>
        <ellipse cx="36" cy="38" rx="8" ry="3"/>
        <ellipse cx="32" cy="44" rx="10" ry="3"/>
      </g>
    </svg>
  );
};

export default Logo;
